import { useUserDirectory } from "@/hooks/use-user-directory";
import { AsyncSelectOptions } from "@/components/ui/FormFields/AsyncSelectOptions";
import MatterNotes from "@/components/features/matter/notes/MatterNotes";
import MatterTasks from "@/components/features/matter/tasks/MatterTasks";
import {
  AppModal,
  ContextMenu,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  SearchableContextMenuSubmenu,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useCreateMatterAssignee } from "@/lib/data/react-query/matters/matter-assignee";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useCreateMatterStatusPhase } from "@/lib/data/react-query/matters/matter-status";
import { type MatterSearchResult } from "@/lib/data/requests/matters/search-matters";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { getMatterStatusCategoryLabel } from "@/lib/data/static/matterStatics";
import { type MatterRole, type MatterStatus } from "@/lib/types";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import { formatUsersName } from "@/lib/utils/format";
import {
  CheckSquare,
  Clipboard,
  FileText,
  ListTodo,
  Mail,
  MessageSquare,
  NotebookPen,
  Phone,
  Plus,
  UserRoundPlus,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";

type LeadRowContextMenuProps = {
  entityName?: "lead" | "opportunity";
  lead: MatterSearchResult;
  matterRoles: MatterRole[];
  matterStatuses: MatterStatus[];
  isMatterStatusesError: boolean;
  isMatterStatusesLoading: boolean;
  trigger: ReactNode;
};

export function LeadRowContextMenu({
  entityName = "lead",
  lead,
  matterRoles,
  matterStatuses,
  isMatterStatusesError,
  isMatterStatusesLoading,
  trigger,
}: LeadRowContextMenuProps) {
  const { hasActionPermission, currentUser } = useUser();
  const {
    openCommunicationModal,
    openNoteModal,
    openSendMatterEmailModal,
    openTaskModal,
  } = useGlobalModal();
  const createMatterStatusPhase = useCreateMatterStatusPhase();
  const createMatterAssignee = useCreateMatterAssignee();
  const [activePanel, setActivePanel] = useState<"notes" | "tasks">();
  const [isAssignMenuOpen, setIsAssignMenuOpen] = useState(false);
  const [statusSearch, setStatusSearch] = useState("");
  const { data: matterData } = useQuery({
    ...retrieveMatterQueryOptions(lead.id),
    enabled: Boolean(activePanel),
  });
  const canChangeStatus = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatusPhase,
    ACTION_PERMISSION_VALUES.create,
  );
  const canCreateTasks = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const canCreateNotes = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const canCreateAssignee = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterAssignee,
    ACTION_PERMISSION_VALUES.create,
  );
  const canSendMatterEmail = true;
  const canTextContact = true;
  const userDirectory = useUserDirectory({
    enabled: canCreateAssignee,
    open: isAssignMenuOpen,
  });
  const {
    users,
    isLoading: isUsersLoading,
    isError: isUsersError,
    search: assignSearch,
    setSearch: setAssignSearch,
  } = userDirectory;
  const client = lead.clients?.[0];
  const clientEmail = client?.emailAddresses?.find(Boolean);
  const clientPhone = client?.phoneNumbers?.find(Boolean);
  const clientPhoneNumber =
    typeof clientPhone === "string" ? clientPhone : clientPhone?.number;
  const entityLabel =
    client?.name?.trim() || lead.title?.trim() || `this ${entityName}`;
  const defaultMatterRole = matterRoles.at(0);
  const statuses = useMemo(() => {
    const search = statusSearch.trim().toLowerCase();
    return search
      ? matterStatuses.filter((status) =>
          status.label.toLowerCase().includes(search),
        )
      : matterStatuses;
  }, [matterStatuses, statusSearch]);
  const currentStatusId = lead.status?.id ?? lead.statusId ?? undefined;
  const assignedUserIds = useMemo(
    () =>
      (lead.assignees ?? [])
        .filter((assignee) => !assignee.dateTimeEnd)
        .map((assignee) => assignee.user.id),
    [lead.assignees],
  );

  return (
    <>
      <ContextMenu entityName={entityName} trigger={trigger}>
        {canChangeStatus ? (
          <SearchableContextMenuSubmenu
            emptyMessage={
              isMatterStatusesError
                ? "Unable to load statuses."
                : "No statuses found."
            }
            getItemKey={(status) => status.id}
            getItemGroup={(status) =>
              getMatterStatusCategoryLabel(status.categoryId)
            }
            isLoading={
              isMatterStatusesLoading || createMatterStatusPhase.isPending
            }
            items={statuses}
            onSearchChange={setStatusSearch}
            onSelectItem={(status) => {
              if (status.id === currentStatusId) {
                return;
              }

              void createMatterStatusPhase.mutateAsync({
                dateTimeStart: new Date().toISOString(),
                matterId: lead.id,
                statusId: status.id,
              });
            }}
            placeholder="Search statuses..."
            renderItem={(status) => status.label}
            search={statusSearch}
            selectedItemKeys={currentStatusId ? [currentStatusId] : []}
            title={
              <>
                <CheckSquare />
                Change {entityName} status
              </>
            }
          />
        ) : null}
        <ContextMenuItem
          disabled={!clientEmail}
          onSelect={() => {
            void copyToClipboard(clientEmail ?? "", "Client email");
          }}
        >
          <Clipboard />
          Copy client email
        </ContextMenuItem>
        <ContextMenuItem
          disabled={!clientPhoneNumber}
          onSelect={() => {
            void copyToClipboard(
              clientPhoneNumber ?? "",
              "Client phone number",
            );
          }}
        >
          <Phone />
          Copy client phone number
        </ContextMenuItem>
        <ContextMenuItem
          disabled={!client?.id || !clientPhoneNumber || !canTextContact}
          onSelect={() => {
            if (!client?.id) return;
            openCommunicationModal({ contactId: client.id, matterId: lead.id });
          }}
        >
          <MessageSquare />
          Send text
        </ContextMenuItem>
        <ContextMenuItem
          disabled={!client?.id || !clientEmail || !canSendMatterEmail}
          onSelect={() => {
            if (!client?.id || !clientEmail) return;
            openSendMatterEmailModal({
              matterId: lead.id,
              contactId: client.id,
              toEmails: [clientEmail],
              templateData: { firm_name: currentUser.firm.name },
              title: "Send email",
              description: "Compose and send an email for this matter.",
            });
          }}
        >
          <Mail />
          Send email
        </ContextMenuItem>
        {canCreateAssignee && defaultMatterRole ? (
          <SearchableContextMenuSubmenu
            emptyMessage={
              isUsersError ? "Unable to load users." : "No users found."
            }
            getItemKey={(user) => user.id}
            disabledItemKeys={assignedUserIds}
            isLoading={isUsersLoading || createMatterAssignee.isPending}
            items={users}
            hasNextPage={userDirectory.hasNextPage}
            isFetchingNextPage={userDirectory.isFetchingNextPage}
            onLoadMore={userDirectory.selectProps.onLoadMore}
            footer={<AsyncSelectOptions {...userDirectory.selectProps} />}
            onOpenChange={(open) => {
              setIsAssignMenuOpen(open);
              if (!open) setAssignSearch("");
            }}
            onSearchChange={setAssignSearch}
            onSelectItem={(user) => {
              void createMatterAssignee.mutateAsync({
                dateTimeStart: new Date().toISOString(),
                matterId: lead.id,
                roleId: defaultMatterRole.id,
                userId: user.id,
              });
            }}
            placeholder="Search users..."
            renderItem={(user) => formatUsersName(user) || user.email}
            search={assignSearch}
            selectedItemKeys={assignedUserIds}
            selectionMode="multiple"
            title={
              <>
                <UserRoundPlus />
                Assign
              </>
            }
          />
        ) : null}
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <CheckSquare />
            Tasks
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            <ContextMenuItem
              onSelect={() => {
                setActivePanel("tasks");
              }}
            >
              <ListTodo />
              Open tasks
            </ContextMenuItem>
            {canCreateTasks ? (
              <ContextMenuItem
                onSelect={() => {
                  openTaskModal({
                    matterId: lead.id,
                    editing: false,
                    initialTab: "details",
                    hideMatterField: true,
                  });
                }}
              >
                <ListTodo />
                Create task
              </ContextMenuItem>
            ) : null}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <NotebookPen />
            Notes
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            <ContextMenuItem
              onSelect={() => {
                setActivePanel("notes");
              }}
            >
              <FileText />
              Open notes
            </ContextMenuItem>
            {canCreateNotes ? (
              <ContextMenuItem
                onSelect={() => {
                  openNoteModal({
                    matterId: lead.id,
                    editing: false,
                    initialTab: "details",
                  });
                }}
              >
                <Plus />
                Create note
              </ContextMenuItem>
            ) : null}
          </ContextMenuSubContent>
        </ContextMenuSub>
      </ContextMenu>
      {matterData?.data ? (
        <>
          <AppModal
            open={activePanel === "tasks"}
            onOpenChange={(open) => {
              if (!open) setActivePanel(undefined);
            }}
            title={`Tasks for ${entityLabel}`}
            size="3xl"
            bodyContained
          >
            <div className="flex h-[min(70vh,42rem)] min-h-0 flex-col">
              <MatterTasks
                tasks={[]}
                matter={matterData.data}
                defaultVariant="list"
                showCreateButton
                showFilters={false}
                bareLayout
                loadMoreMode="infinite"
              />
            </div>
          </AppModal>
          <AppModal
            open={activePanel === "notes"}
            onOpenChange={(open) => {
              if (!open) setActivePanel(undefined);
            }}
            title={`Notes for ${entityLabel}`}
            size="3xl"
            bodyContained
          >
            <div className="flex h-[min(80vh,52rem)] min-h-0 flex-col p-4">
              <MatterNotes notes={[]} matter={matterData.data} bareLayout />
            </div>
          </AppModal>
        </>
      ) : null}
    </>
  );
}
